import { useState, useCallback, useEffect } from 'react';
import { ticketsApi } from '../../../services/apiClient';

/**
 * Hook for managing ticket interventions
 */
export const useInterventions = (ticketId) => {
  const [linkedInterventions, setLinkedInterventions] = useState([]);
  const [linkedIntervention, setLinkedIntervention] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Request intervention state
  const [sending, setSending] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Fetch interventions for a ticket
  const fetchInterventions = useCallback(async () => {
    if (!ticketId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await ticketsApi.getInterventions(ticketId);
      const interventions = response.data?.interventions || response.data?.data || response.data || [];

      // Sort by created_at descending (most recent first) to match old code behavior
      const sortedInterventions = Array.isArray(interventions)
        ? [...interventions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        : [];

      setLinkedInterventions(sortedInterventions);

      // Set the most recent intervention (first after sorting)
      setLinkedIntervention(sortedInterventions.length > 0 ? sortedInterventions[0] : null);
    } catch (err) {
      console.error('Error fetching interventions:', err);
      setError('Failed to load interventions');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Request a new intervention
  const requestIntervention = useCallback(async ({ date, time, deviceId, notes }) => {
    if (!ticketId) return { success: false, error: 'No ticket ID' };

    try {
      setSending(true);

      // Build datetime if date is provided
      let scheduledFor = null;
      if (date) {
        const timeValue = time || '09:00';
        scheduledFor = new Date(`${date}T${timeValue}`).toISOString();
      }

      const response = await ticketsApi.requestIntervention(ticketId, {
        scheduled_for: scheduledFor,
        device_id: deviceId || null,
        notes: notes || null
      });

      // Refresh interventions
      await fetchInterventions();

      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error requesting intervention:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to request intervention'
      };
    } finally {
      setSending(false);
    }
  }, [ticketId, fetchInterventions]);

  // Reschedule an intervention
  const rescheduleIntervention = useCallback(async (interventionId, { date, time, notes }) => {
    if (!interventionId) return { success: false, error: 'No intervention ID' };

    try {
      setRescheduling(true);

      const timeValue = time || '09:00';
      const scheduledFor = new Date(`${date}T${timeValue}`).toISOString();

      const response = await ticketsApi.rescheduleIntervention(ticketId, interventionId, {
        scheduled_for: scheduledFor,
        notes: notes || null
      });

      // Refresh interventions
      await fetchInterventions();

      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error rescheduling intervention:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to reschedule intervention'
      };
    } finally {
      setRescheduling(false);
    }
  }, [ticketId, fetchInterventions]);

  // Cancel an intervention
  const cancelIntervention = useCallback(async (interventionId, reason) => {
    if (!interventionId) return { success: false, error: 'No intervention ID' };
    if (!reason || reason.trim() === '') return { success: false, error: 'Cancellation reason is required' };

    try {
      setCancelling(true);

      const response = await ticketsApi.cancelIntervention(ticketId, interventionId, {
        reason: reason
      });

      // Refresh interventions
      await fetchInterventions();

      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error cancelling intervention:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to cancel intervention'
      };
    } finally {
      setCancelling(false);
    }
  }, [ticketId, fetchInterventions]);

  // Check if there's a pending or scheduled intervention
  const hasPendingIntervention = linkedIntervention &&
    ['pending', 'scheduled', 'in_progress'].includes(linkedIntervention.status);

  // Check if there's a completed intervention
  const hasCompletedIntervention = linkedInterventions.some(i => i.status === 'completed');

  // Fetch on mount
  useEffect(() => {
    if (ticketId) {
      fetchInterventions();
    }
  }, [ticketId, fetchInterventions]);

  return {
    linkedInterventions,
    linkedIntervention,
    loading,
    error,
    sending,
    rescheduling,
    cancelling,
    hasPendingIntervention,
    hasCompletedIntervention,
    fetchInterventions,
    requestIntervention,
    rescheduleIntervention,
    cancelIntervention
  };
};

export default useInterventions;
